import React from 'react';
import {Form, Formik, FormikHelpers} from "formik";
import {AudienceSegment, AudienceSegmentData} from "types/AudienceSegment";
import {useNavigate} from "react-router-dom";
import Button from "components/Button";
import Field from "components/Form/Field";
import CriteriaBuilder from "components/Form/CriteriaBuilder";
import * as Yup from "yup";

// Types ----

type AudienceSegmentFormProps = {
  audienceSegment?: AudienceSegment
  onSubmit: (values: AudienceSegmentData, helpers: FormikHelpers<AudienceSegmentData>) => Promise<any>
}

// Component ----

function AudienceSegmentForm({audienceSegment, onSubmit}: AudienceSegmentFormProps) {
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("This field is required")
      .max(255, "Maximum of 255 characters"),
    criteria: Yup.object().shape({
      groups: Yup.array().of(Yup.object().shape({
        filters: Yup.object().test('at-least-one-filter', 'Choose at least one filter', filters => {
            return Object.keys(filters).length > 0;
          }
        )
      }))
    })
  })

  const initialValues: AudienceSegmentData = {
    name: audienceSegment?.name || '',
    criteria: audienceSegment?.criteria || {groups: [{filters: {}}]}
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the audience segment
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-4">
                        <div className="mt-1">
                          <Field
                            label='Name *'
                            hint='255 chars max'
                            type="text"
                            name="name"
                            id="name"
                            autoComplete="off"
                            maxLength="255"
                            autoFocus
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Criteria
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Defines which audience members are part of this segment
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <CriteriaBuilder formik={formik}/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-5 bg-gray-50 text-right sm:px-6 sm:rounded-b-md">
                <div className="flex space-x-3 justify-end">
                  <Button color={Button.colors.WHITE} onClick={() => navigate(-1)} disabled={formik.isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    showSpinner={formik.isSubmitting}
                    disabled={formik.isSubmitting}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

// Export ----

export default AudienceSegmentForm;